import React, { useEffect, useMemo, useState } from "react";
import { Cpu, Smartphone } from "lucide-react";
import { Button } from "./ui/button";
import { Carousel, CarouselContent, CarouselItem } from "./ui/carousel";
import { Link } from "react-router-dom";

const ShopPromoBanner = () => {
  const publicUrl = process.env.PUBLIC_URL || "";
  const slides = useMemo(
    () => [
      {
        id: "amd",
        eyebrow: "AMD Highlights",
        title: "Radeon listas para jugar",
        description: "Rendimiento en 2K y 4K con VRAM generosa y drivers optimizados.",
        cta: "Ver AMD",
        ctaLink: "/tienda?categoria=Placas%20de%20Video&marca=AMD#catalogo",
        image: `${publicUrl}/images/ads/amd-banner.png`,
        mobileImage: `${publicUrl}/images/ads/amd-banner-1200.jpg`,
        position: "center",
        icon: Cpu
      },
      {
        id: "nvidia",
        eyebrow: "NVIDIA GeForce",
        title: "Potencia RTX para creators",
        description: "Ray tracing, DLSS y desempeño estable para streaming y edición.",
        cta: "Ver NVIDIA",
        ctaLink: "/tienda?categoria=Placas%20de%20Video&marca=NVIDIA#catalogo",
        image: `${publicUrl}/images/ads/nvidia-banner.jpg`,
        mobileImage: `${publicUrl}/images/ads/nvidia-banner-1200.jpg`,
        position: "center 28%",
        icon: Cpu
      },
      {
        id: "iphone",
        eyebrow: "Apple Essentials",
        title: "iPhone en stock",
        description: "Modelos actuales con asesoría y configuración personalizada.",
        cta: "Ver iPhone",
        ctaLink: "/tienda?categoria=iPhones#catalogo",
        image: `${publicUrl}/images/ads/iphone-banner.jpg`,
        mobileImage: `${publicUrl}/images/ads/iphone-banner-1200.jpg`,
        position: "center 25%",
        icon: Smartphone
      }
    ],
    [publicUrl]
  );

  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return undefined;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    const interval = setInterval(() => api.scrollNext(), 6500);
    return () => {
      api.off("select", onSelect);
      clearInterval(interval);
    };
  }, [api]);

  return (
    <section className="pt-0 pb-6 px-0">
      <div className="w-full">
        <div className="relative overflow-hidden border border-border shadow-2xl">
          <div className="relative z-10">
            <Carousel opts={{ loop: true }} setApi={setApi} className="relative">
              <CarouselContent className="ml-0">
                {slides.map((slide, index) => {
                  const Icon = slide.icon;
                  return (
                    <CarouselItem key={slide.id} className="pl-0">
                      <div className="relative w-full overflow-hidden h-[50vh] min-h-[260px]">
                        <picture>
                          <source media="(max-width: 768px)" srcSet={slide.mobileImage} />
                          <img
                            className="absolute inset-0 h-full w-full object-cover"
                            src={slide.image}
                            alt=""
                            aria-hidden="true"
                            loading={current === index ? "eager" : "lazy"}
                            decoding="async"
                            style={{ objectPosition: slide.position }}
                          />
                        </picture>
                        <div className="absolute inset-0 bg-black/10" />
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.15),_transparent_60%)]" />

                        <div className="relative z-10 h-full w-full p-6 sm:p-10 flex flex-col justify-end gap-4 text-white drop-shadow-[0_6px_18px_rgba(0,0,0,0.55)]">
                          <div className="text-[11px] uppercase tracking-[0.35em] text-white/80">
                            {slide.eyebrow}
                          </div>
                          <div className="max-w-xl space-y-3">
                            <h2 className="text-2xl sm:text-4xl font-semibold leading-tight">
                              {slide.title}
                            </h2>
                            <p className="text-sm sm:text-base text-white/85">
                              {slide.description}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            <Button asChild className="rounded-full bg-white text-slate-900 hover:bg-white/90">
                              <Link to={slide.ctaLink}>{slide.cta}</Link>
                            </Button>
                            <div className="flex items-center gap-2 text-xs text-white/70">
                              <Icon className="h-4 w-4" />
                              <span>Asesoría rápida en tienda</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>

              {/* Sin flechas por requerimiento */}
            </Carousel>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-2">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => api?.scrollTo(index)}
              aria-label={`Ir al slide ${index + 1}`}
              className={`h-2 w-8 rounded-full transition-all duration-300 ${
                current === index ? "bg-foreground" : "bg-foreground/30"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ShopPromoBanner;
